<template>
  <ul
    :class="{
      'mobile-navigation-bar-fi': true,
      'mobile-navigation-bar-fi--search-active': isSearchActive,
    }"
  >
    <li
      key="search"
      ref="searchListItem"
      :class="{
        'mobile-navigation-bar-fi__item': true,
        'mobile-navigation-bar-fi__item--icon': true,
        'mobile-navigation-bar-fi__search-item': true,
        'mobile-navigation-bar-fi__search-item--hidden': isSearchActive || isSearchTransitioning,
      }"
    >
      <div
        class="mobile-navigation-bar-fi__item__icon mobile-navigation-bar-fi__search-icon"
        data-test="naviation"
        data-test-2="mobile-search-icon"
        @click="openSearch"
      >
        <icon-fi
          class="mobile-navigation-bar-fi__item__icon"
          icon="search"
        />
      </div>
    </li>
    <template v-for="navigation in serviceNavigationHeader">
      <li
        v-for="navigationElement in navigation.elements"
        :key="`${navigation.title}${navigationElement.identifier}`"
        :class="{
          'mobile-navigation-bar-fi__item': true,
          'mobile-navigation-bar-fi__item--icon': true,
          [`mobile-navigation-bar-fi__item-${navigation.title}-${navigationElement.identifier}`]: true,
        }"
      >
        <service-navigation-item-fi
          :navigation-element="navigationElement"
          :brand-background="brandBackground"
          @login="$emit('login')"
        />
      </li>
    </template>
    <li
      v-if="!showFlyout && !isCountrySelect"
      class="mobile-navigation-bar-fi__item mobile-navigation-bar-fi__item--icon"
      data-test="naviation"
      data-test-2="mobile-menu-open"
      @click="showMobileFlyout"
    >
      <icon-fi
        class="mobile-navigation-bar-fi__item__icon"
        icon="menu"
      />
    </li>
    <li
      v-else
      class="mobile-navigation-bar-fi__item mobile-navigation-bar-fi__item--icon"
      data-test="naviation"
      data-test-2="mobile-menu-close"
      @click="hideMobileFlyout"
    >
      <icon-fi
        class="mobile-navigation-bar-fi__item__icon"
        icon="close"
      />
    </li>
  </ul>
</template>

<script setup lang="ts">
import { IconFi } from 'atoms';
import { computed, watch } from 'vue';

import { useBodyScroll } from '@/composables/bodyScroll';
import { useBreakpoints } from '@/composables/breakpoint';
import { useNavigationStore } from '@/store/pinia/navigation';

import ServiceNavigationItemFi from '../service-navigation-item-fi/ServiceNavigationItemFi.vue';

defineOptions({ name: 'MobileNavigationBarFi' });

interface Props {
  serviceNavigation: any[];
  brandBackground?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  brandBackground: false,
});

defineEmits<{ login: [] }>();

const navigationStore = useNavigationStore();
const { isSM, isMD } = useBreakpoints();
const { bodyDisableScroll, bodyEnableScroll } = useBodyScroll();

const showFlyout = computed(() => navigationStore.showFlyout);
const isSearchActive = computed(() => navigationStore.searchActive);
const isSearchTransitioning = computed(() => navigationStore.searchTransitioning);
const isSearchSuggestions = computed(() => navigationStore.showSearchSuggest);
const isMobileFirstLvl = computed(() => navigationStore.showMobileFirstLvl);
const isMobileSecondLvl = computed(() => navigationStore.showMobileSubLvl);
const isCountrySelect = computed(() => navigationStore.isCountrySelect);

const serviceNavigationHeader = computed(() =>
  props.serviceNavigation
    .map((item: any) => ({
      ...item,
      elements: item.elements.filter((element: any) => !element.hideFromMobile),
    }))
    .filter((item: any) => item.elements.length),
);

const isFlyoutActive = computed(
  () => isSearchSuggestions.value || isMobileFirstLvl.value || isMobileSecondLvl.value,
);

const isNavigationActive = computed(
  () =>
    isSearchActive.value ||
    isMobileFirstLvl.value ||
    isMobileSecondLvl.value ||
    showFlyout.value ||
    isCountrySelect.value,
);

watch(
  isFlyoutActive,
  (value) => {
    if (value && !isMD.value) {
      bodyDisableScroll();
    } else {
      bodyEnableScroll();
    }
  },
  { immediate: true },
);

watch(
  isNavigationActive,
  (value) => {
    navigationStore.setShowBrandArea(!value);
  },
  { immediate: true },
);

watch(isSearchActive, (value) => {
  if (!value) {
    hideMobileFlyout();
  }
});

const openSearch = () => {
  if (isSearchActive.value) {
    return;
  }
  navigationStore.setSearchInput(true);
};

const showMobileFlyout = () => {
  navigationStore.setShowMobileFirstLvl(true);
};

const hideMobileFlyout = () => {
  navigationStore.setIsCountrySelect(false);
  if (isSM.value) {
    navigationStore.setShowFlyout(false);
  } else {
    navigationStore.setShowMobileSubLvl(false);
  }
};
</script>

<style scoped src="./mobile-navigation-bar-fi" />
